import { useState, type FormEvent } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'

import { connecter } from '../auth/connexion'
import { useSession } from '../auth/session'

/**
 * Page de connexion.
 *
 * Courriel, mot de passe, un bouton. Pas de creation de compte, pas de lien
 * magique, pas de fournisseur tiers : chacun serait une porte d'entree de plus
 * a surveiller, pour un outil interne d'une vingtaine de personnes.
 */

const MESSAGE_ECHEC = 'Courriel ou mot de passe incorrect.'

export default function Connexion() {
  const etat = useSession()
  const navigate = useNavigate()
  const emplacement = useLocation()

  const [courriel, setCourriel] = useState('')
  const [motDePasse, setMotDePasse] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const [enCours, setEnCours] = useState(false)

  const destination =
    (emplacement.state as { from?: string } | null)?.from ?? '/'

  if (etat.statut === 'connecte') {
    return <Navigate to={destination} replace />
  }

  async function soumettre(evenement: FormEvent<HTMLFormElement>): Promise<void> {
    evenement.preventDefault()
    if (enCours) return

    setEnCours(true)
    setErreur(null)

    const resultat = await connecter(courriel.trim(), motDePasse)

    if (!resultat.ok) {
      // Un seul message, pour un courriel inconnu comme pour un mot de passe
      // faux. Distinguer les deux transformerait le formulaire en annuaire des
      // comptes existants.
      setErreur(MESSAGE_ECHEC)
      setEnCours(false)
      return
    }

    setEnCours(false)
    navigate(destination, { replace: true })
  }

  return (
    <main className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-sm">
        {/* La marine est decorative : un filet, jamais du texte. */}
        <div className="h-1 rounded-full bg-marque-accent" aria-hidden="true" />

        <h1 className="mt-6 text-xl font-semibold text-slate-900">Dossiers CV</h1>
        <p className="mt-1 text-sm text-slate-600">Evol Formation — accès réservé.</p>

        {etat.statut === 'deconnecte' && etat.motif === 'acces-desactive' ? (
          <p
            role="alert"
            data-test="acces-desactive"
            className="mt-6 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
          >
            Votre accès a été désactivé, contactez un administrateur.
          </p>
        ) : null}

        <form onSubmit={soumettre} className="mt-6 space-y-4" noValidate>
          <div>
            <label htmlFor="courriel" className="block text-sm font-medium text-slate-700">
              Courriel
            </label>
            <input
              id="courriel"
              name="courriel"
              type="email"
              autoComplete="username"
              required
              value={courriel}
              onChange={(e) => setCourriel(e.target.value)}
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-slate-900 focus:border-marque-primaire-texte focus:outline-none focus:ring-2 focus:ring-marque-primaire-texte"
            />
          </div>

          <div>
            <label htmlFor="mot-de-passe" className="block text-sm font-medium text-slate-700">
              Mot de passe
            </label>
            <input
              id="mot-de-passe"
              name="mot-de-passe"
              type="password"
              autoComplete="current-password"
              required
              value={motDePasse}
              onChange={(e) => setMotDePasse(e.target.value)}
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-slate-900 focus:border-marque-primaire-texte focus:outline-none focus:ring-2 focus:ring-marque-primaire-texte"
            />
          </div>

          {erreur ? (
            <p role="alert" data-test="erreur-connexion" className="text-sm text-red-700">
              {erreur}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={enCours}
            data-test="bouton-connexion"
            className="w-full rounded bg-marque-primaire-texte px-4 py-2 font-medium text-white hover:bg-marque-primaire-survol disabled:cursor-not-allowed disabled:opacity-60"
          >
            {enCours ? 'Connexion…' : 'Se connecter'}
          </button>
        </form>
      </div>
    </main>
  )
}
